import React from 'react';
import {Screen, StatisticWidgetDef} from '../types';
import {formatStatisticCell, toNumber} from '../utils/statisticValues';
import {StatisticChart} from './StatisticChart';

export const widgetTitle = (widget: StatisticWidgetDef, screen: Screen) => widget.displayName || screen.displayName;

const isNumericCell = (value: any) =>
    typeof value === 'number' || (typeof value === 'string' && /^-?\d+(\.\d+)?$/.test(value) && toNumber(value) !== null);

/**
 * Compact table; scrolls inside the widget's height. A column whose filled
 * values are all numbers is right-aligned as a whole — header included — so
 * the label sits above its figures.
 */
const WidgetTable: React.FC<{columns: string[]; rows: Record<string, any>[]}> = ({columns, rows}) => {
    const numeric = new Set(columns.filter((column) => {
        const filled = rows.map((row) => row[column]).filter((value) => value !== null && value !== undefined && value !== '');
        return filled.length > 0 && filled.every(isNumericCell);
    }));
    const align = (column: string) => (numeric.has(column) ? 'is-number' : undefined);

    return (
        <div className="statistic-widget-table">
            <table>
                <thead>
                    <tr>{columns.map((column) => <th key={column} className={align(column)}>{column}</th>)}</tr>
                </thead>
                <tbody>
                    {rows.map((row, index) => (
                        <tr key={index}>
                            {columns.map((column) => (
                                <td key={column} className={align(column)}>{formatStatisticCell(row[column])}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

/**
 * Body of one statistic widget over an already loaded result: the table (the
 * widget's columns, all when none are picked) or the chart.
 */
export const StatisticWidgetView: React.FC<{
    widget: StatisticWidgetDef;
    columns: string[];
    rows: Record<string, any>[];
    height: number | 'fill';
}> = ({widget, columns, rows, height}) => {
    if (rows.length === 0) {
        return <p className="statistic-widget-note">Žiadne dáta</p>;
    }
    if (widget.type === 'table') {
        const shown = widget.columns.length > 0 ? widget.columns.filter((column) => columns.includes(column)) : columns;
        const table = <WidgetTable columns={shown.length > 0 ? shown : columns} rows={widget.limit ? rows.slice(0, widget.limit) : rows}/>;
        return height === 'fill' ? table : <div style={{maxHeight: height, overflow: 'auto'}}>{table}</div>;
    }
    return <StatisticChart chart={{type: widget.type, x: widget.x, y: widget.y, limit: widget.limit}} columns={columns} rows={rows} height={height}/>;
};
